import React from 'react';
import { useTranslation } from 'react-i18next';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList, Legend } from 'recharts';

// Brand palette for multi-series bars — matches HeedifyTheme.css
const SERIES_COLORS = [
    '#1B3A6B', // Navy   — Inbound / primary series
    '#00A4A6', // Teal   — Outbound / secondary series
    '#F97316', // Orange
    '#8B5CF6', // Violet
    '#10b981', // Green
];

const BAR_RADIUS = [6, 6, 0, 0]; // rounded top corners

const BarChartWidget = ({ data, config }) => {
    const { t } = useTranslation();

    const xAxisKey        = config?.config?.xAxisKey        || config?.xAxisKey        || 'name';
    const yAxisKey        = config?.config?.yAxisKey        || config?.yAxisKey        || 'value';
    const secondaryYAxisKey = config?.config?.secondaryYAxisKey;
    const seriesKeys      = config?.config?.seriesKeys;
    const seriesNames     = config?.config?.seriesNames;
    const stacked         = config?.config?.stacked         || false;
    const horizontal      = config?.config?.horizontal      || config?.horizontal      || false;
    const xAxisFormatter  = config?.config?.xAxisFormatter  || config?.xAxisFormatter;
    const showValues      = config?.config?.showValues      !== false;
    const valueFormat     = config?.config?.valueFormat     || 'duration';
    const yAxisLabel      = config?.config?.yAxisLabel      || (valueFormat === 'duration' ? t('WAIT_SECONDS') : '');
    const tooltipSuffix   = config?.config?.tooltipSuffix   || (valueFormat === 'number' ? ' calls' : '');
    const alignTicksLeft  = config?.config?.alignTicksLeft  || false;

    const chartData = Array.isArray(data) ? data : [];

    if (chartData.length === 0) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <p style={{ color: '#605E5C' }}>{t('NO_DATA_AVAILABLE')}</p>
            </div>
        );
    }

    // Domain calculation
    let primaryValues;
    if (seriesKeys?.length > 0) {
        primaryValues = chartData.flatMap(item => seriesKeys.map(key => item[key] || 0));
    } else {
        primaryValues = chartData.map(item => item[yAxisKey] || 0);
    }
    const maxPrimary = Math.max(...primaryValues);
    const primaryDomain = [0, Math.ceil(maxPrimary * 1.2)];

    let secondaryDomain = [0, 100];
    if (secondaryYAxisKey) {
        const secondaryValues = chartData.map(item => item[secondaryYAxisKey] || 0);
        secondaryDomain = [0, Math.ceil(Math.max(...secondaryValues)) + 1];
    }

    const formatValue = (value) => {
        if (valueFormat === 'number') return Math.round(value);
        const totalSeconds = Math.round(value);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const formatValueHideZero = (value) => (!value || value === 0) ? '' : formatValue(value);

    // Label renderer: fixed font size for all bars (homogeneous), hidden when bar too small
    const renderBarLabel = (fmt) => (props) => {
        const { x, y, width, height, value } = props;
        const formatted = fmt(value);
        if (!formatted || height < 18) return null;
        return (
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#ffffff"
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize={10}
                fontWeight={700}
            >
                {formatted}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null;
        return (
            <div style={{
                background: '#fff',
                padding: '10px 14px',
                border: '1px solid #e1e1e1',
                borderRadius: 6,
                fontSize: 13,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
            }}>
                <p style={{ margin: '0 0 6px', fontWeight: 600, color: '#323130' }}>
                    {xAxisFormatter ? xAxisFormatter(label) : label}
                </p>
                {payload.map((entry, index) => {
                    const formattedValue = (valueFormat === 'number' || seriesKeys)
                        ? Math.round(entry.value)
                        : entry.dataKey === yAxisKey ? formatValue(entry.value) : Math.round(entry.value);
                    return (
                        <p key={index} style={{ margin: '3px 0', color: entry.color }}>
                            {entry.name}: <strong>{formattedValue}</strong>
                            {seriesKeys && tooltipSuffix}
                            {entry.dataKey === secondaryYAxisKey ? ` ${t('CALLS').toLowerCase()}` : ''}
                        </p>
                    );
                })}
            </div>
        );
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
                data={chartData}
                margin={{ top: 20, right: 40, left: 0, bottom: 5 }}
                barCategoryGap="8%"
                barGap={3}
            >
                <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e8e8e8"
                    vertical={false}
                />
                <XAxis
                    dataKey={xAxisKey}
                    tickFormatter={xAxisFormatter}
                    tick={alignTicksLeft
                        ? (props) => {
                            const { x, y, payload } = props;
                            const halfBand = payload?.offset || 0;
                            const firstBarLeft = x - halfBand * 0.92;
                            const rawLabel = String(xAxisFormatter ? xAxisFormatter(payload.value) : payload.value);
                            const maxWidth = Math.max(0, halfBand * 2 - 6);
                            const maxChars = Math.max(3, Math.floor(maxWidth / 6.5));
                            const displayLabel = rawLabel.length > maxChars
                                ? rawLabel.slice(0, Math.max(1, maxChars - 1)) + '…'
                                : rawLabel;
                            return (
                                <text
                                    x={firstBarLeft}
                                    y={y + 14}
                                    textAnchor="start"
                                    fontSize={12}
                                    fill="#605E5C"
                                >
                                    {displayLabel}
                                    <title>{rawLabel}</title>
                                </text>
                            );
                        }
                        : { fontSize: 12, fill: '#605E5C' }
                    }
                    axisLine={false}
                    tickLine={false}
                />
                <YAxis
                    yAxisId="left"
                    domain={primaryDomain}
                    tick={{ fontSize: 12, fill: '#605E5C' }}
                    axisLine={false}
                    tickLine={false}
                    label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#605E5C' } } : undefined}
                />
                {secondaryYAxisKey && (
                    <YAxis
                        yAxisId="right"
                        orientation="right"
                        domain={secondaryDomain}
                        allowDataOverflow={true}
                        tick={{ fontSize: 12, fill: '#605E5C' }}
                        axisLine={false}
                        tickLine={false}
                        label={{ value: t('CALLS'), angle: 90, position: 'insideRight', style: { fontSize: 11, fill: '#605E5C' } }}
                    />
                )}
                <Tooltip content={<CustomTooltip />} />
                {config?.config?.showLegend !== false && (
                    <Legend
                        wrapperStyle={{ fontSize: 13, paddingTop: 8 }}
                    />
                )}

                {/* Multiple series */}
                {seriesKeys?.length > 0 ? (
                    seriesKeys.map((key, index) => (
                        <Bar
                            key={key}
                            yAxisId="left"
                            dataKey={key}
                            name={seriesNames?.[index] ? t(seriesNames[index]) : key}
                            fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                            radius={BAR_RADIUS}
                            stackId={stacked ? 'stack' : undefined}
                        >
                            {showValues && (
                                <LabelList
                                    dataKey={key}
                                    content={renderBarLabel(formatValueHideZero)}
                                />
                            )}
                        </Bar>
                    ))
                ) : (
                    /* Single bar */
                    <Bar
                        yAxisId="left"
                        dataKey={yAxisKey}
                        name={config?.config?.primaryYAxisName ? t(config.config.primaryYAxisName) : (config?.titleKey ? t(config.titleKey) : (config?.title || yAxisKey))}
                        fill="#1B3A6B"
                        radius={BAR_RADIUS}
                    >
                        {showValues && (
                            <LabelList
                                dataKey={yAxisKey}
                                content={renderBarLabel(formatValue)}
                            />
                        )}
                    </Bar>
                )}

                {/* Secondary line (e.g. call volume overlay) */}
                {secondaryYAxisKey && (
                    <Line
                        yAxisId="right"
                        type="monotone"
                        dataKey={secondaryYAxisKey}
                        name={t('CALL_VOLUME')}
                        stroke="#F97316"
                        strokeWidth={2.5}
                        dot={{ fill: '#F97316', r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 6 }}
                    />
                )}
            </ComposedChart>
        </ResponsiveContainer>
    );
};

export default BarChartWidget;
